// src/dashboard/pages/teacher/Techniques/utils/techniquesDashboardUtils.js

import { toAbsoluteTechniquePhotoUrl } from "../../../../../services/techniquesPhotoServices";

export const DEFAULT_TECHNIQUE_IMAGE = "/img/no-image.jpg";

export function normalizeString(value) {
  return String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim();
}

export function normalizeFamily(value = "") {
  return String(value || "").trim().replace(/\s+/g, " ");
}

export function getTechniquePhotoSrc(imageUrl) {
  if (!imageUrl) return DEFAULT_TECHNIQUE_IMAGE;

  return toAbsoluteTechniquePhotoUrl(imageUrl) || DEFAULT_TECHNIQUE_IMAGE;
}

export function withCacheBust(url, version) {
  if (!url || !version) return url;
  return `${url}${String(url).includes("?") ? "&" : "?"}v=${version}`;
}

export function beltId(belt) {
  return (
    belt?.id ??
    belt?.belt_id ??
    belt?.ordre ??
    belt?.order ??
    belt?.rank ??
    Number.MAX_SAFE_INTEGER
  );
}

export function beltLabel(belt) {
  return (
    belt?.label ||
    belt?.nom ||
    belt?.name ||
    belt?.belt_name ||
    belt?.couleur ||
    belt?.color ||
    belt?.belt ||
    String(belt || "")
  );
}

export function getFamilyOptions(techniques = []) {
  const families = new Set();

  techniques.forEach((technique) => {
    const family = normalizeFamily(technique?.famille);
    if (family) families.add(family);
  });

  return [...families].sort((a, b) =>
    a.localeCompare(b, "fr", { sensitivity: "base" })
  );
}

export function getBeltOptions(belts = [], techniques = []) {
  const fromBelts = Array.isArray(belts)
    ? belts
        .map((belt) => ({
          id: beltId(belt),
          label: String(beltLabel(belt) || "").trim(),
        }))
        .filter((belt) => belt.label)
        .sort((a, b) => Number(a.id) - Number(b.id))
    : [];

  const labels = new Set(fromBelts.map((belt) => belt.label));

  techniques.forEach((technique) => {
    const belt = String(technique?.ceinture || "").trim();
    if (belt && !labels.has(belt)) {
      labels.add(belt);
      fromBelts.push({
        id: Number.MAX_SAFE_INTEGER,
        label: belt,
      });
    }
  });

  return fromBelts.sort((a, b) => {
    const idDiff = Number(a.id) - Number(b.id);
    if (idDiff !== 0) return idDiff;

    return a.label.localeCompare(b.label, "fr", { sensitivity: "base" });
  });
}

export function sortTechniques(items = [], sort = "name_asc") {
  return [...items].sort((a, b) => {
    if (sort === "name_desc") {
      return String(b?.nom || "").localeCompare(String(a?.nom || ""), "fr", {
        sensitivity: "base",
      });
    }

    if (sort === "family_asc") {
      return String(a?.famille || "").localeCompare(
        String(b?.famille || ""),
        "fr",
        { sensitivity: "base" }
      );
    }

    if (sort === "belt_asc") {
      return String(a?.ceinture || "").localeCompare(
        String(b?.ceinture || ""),
        "fr",
        { sensitivity: "base" }
      );
    }

    return String(a?.nom || "").localeCompare(String(b?.nom || ""), "fr", {
      sensitivity: "base",
    });
  });
}

export function filterTechniques(items = [], filters = {}) {
  const query = normalizeString(filters.query);
  const family = String(filters.family || "");
  const belt = String(filters.belt || "");
  const media = String(filters.media || "");

  const filtered = items.filter((technique) => {
    const searchable = normalizeString(
      [
        technique?.nom,
        technique?.famille,
        technique?.ceinture,
        technique?.traduction,
        technique?.information,
        technique?.uv2,
      ]
        .filter(Boolean)
        .join(" ")
    );

    const matchesQuery = !query || searchable.includes(query);
    const matchesFamily = !family || String(technique?.famille || "") === family;
    const matchesBelt = !belt || String(technique?.ceinture || "") === belt;

    const hasImage = !!String(technique?.image1_url || "").trim();
    const hasVideo = !!String(technique?.video_url || "").trim();
    const hasUv2 = !!String(technique?.uv2 || "").trim();

    const matchesMedia =
      !media ||
      (media === "image" && hasImage) ||
      (media === "video" && hasVideo) ||
      (media === "uv2" && hasUv2) ||
      (media === "without_image" && !hasImage);

    return matchesQuery && matchesFamily && matchesBelt && matchesMedia;
  });

  return sortTechniques(filtered, filters.sort);
}

export function calculateTechniqueStats(allItems = [], filteredItems = []) {
  const families = new Set();
  const belts = new Set();

  let withImage = 0;
  let withVideo = 0;
  let withUv2 = 0;
  let withoutImage = 0;

  allItems.forEach((technique) => {
    const family = normalizeFamily(technique?.famille);
    const belt = String(technique?.ceinture || "").trim();

    if (family) families.add(family);
    if (belt) belts.add(belt);

    if (String(technique?.image1_url || "").trim()) {
      withImage += 1;
    } else {
      withoutImage += 1;
    }

    if (String(technique?.video_url || "").trim()) withVideo += 1;
    if (String(technique?.uv2 || "").trim()) withUv2 += 1;
  });

  return {
    total: allItems.length,
    filtered: filteredItems.length,
    families: families.size,
    belts: belts.size,
    withImage,
    withoutImage,
    withVideo,
    withUv2,
  };
}

export function buildTechniquePayload(formData = {}) {
  const imageValue = String(formData.image1_url || "").trim();

  return {
    nom: String(formData.nom || "").trim(),
    famille: normalizeFamily(formData.famille) || null,
    ceinture: String(formData.ceinture || "").trim() || null,
    image1_url: imageValue || DEFAULT_TECHNIQUE_IMAGE,
    video_url: String(formData.video_url || "").trim() || null,
    traduction: String(formData.traduction || "").trim() || null,
    information: String(formData.information || "").trim() || null,
    uv2: String(formData.uv2 || "").trim() || null,
  };
}